import React, { useState } from 'react'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputBase,
  InputBaseProps,
  InputLabel,
  styled,
} from '@mui/material'

import { CheckIcon } from '~assets/icons'
import { lightTheme } from '~createTheme'

interface TextInputProps extends InputBaseProps {
  placeholder?: string
  label?: string
  helperText?: string
  errorText?: string
  showCopyButton?: boolean
}

const StyledFormControl = styled(FormControl)({
  width: '100%',
  position: 'relative',
  margin: lightTheme.spacing(0, 0),
})

const CopyButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: '8px',
  top: '50%',
  transform: 'translateY(-50%)',
  padding: '4px',
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

const TextInputBase = styled(InputBase)(({ theme, error, readOnly }) => ({
  width: '100%',
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    borderRadius: 4,
    position: 'relative',
    border: '1px solid',
    borderColor: error
      ? theme.palette.error.main
      : readOnly
        ? theme.palette.text.disabled
        : theme.palette.divider,
    backgroundColor: readOnly ? theme.palette.action.disabledInput : 'white',
    color: readOnly ? theme.palette.text.secondary : theme.palette.text.primary,
    fontSize: 16,
    padding: '10px 12px',
    transition:
      !readOnly && theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    fontFamily: theme.typography.body2.fontFamily,
    '&:hover': !readOnly && {
      borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
    },
    '&:focus-visible': !readOnly && {
      border: '2px solid #ffffff',
      outline: error ? `2px solid ${theme.palette.error.main}` : '2px solid #0894cf',
      boxShadow: error
        ? '0 0 0 2px #ee2a7b1d, 0 0 0 4px #ee2a7b1d'
        : '0 0 0 2px #98e0ff, 0 0 0 4px #98e0ff',
    },
  },
}))

export const InputLabelStyled = styled(InputLabel)(({ theme }) => ({
  fontSize: 18,
  letterSpacing: theme.typography.body2.letterSpacing,
  fontFamily: theme.typography.fontFamily,
  '&.Mui-focused': {
    color: theme.palette.text.primary,
  },
}))

const TextInput: React.FC<TextInputProps> = ({
  label,
  placeholder = '',
  type,
  helperText,
  errorText,
  showCopyButton = false,
  ...props
}) => {
  const [copied, setCopied] = useState<boolean>(false)
  const wheelHandler =
    type === 'number'
      ? { onWheel: (e: React.WheelEvent): void => (e.target as HTMLElement).blur() }
      : {}

  const handleCopy = async (): Promise<void> => {
    if (props.value) {
      try {
        await navigator.clipboard.writeText(props.value.toString())
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy text:', err)
      }
    }
  }

  return (
    <StyledFormControl variant='standard'>
      {label && (
        <InputLabelStyled
          htmlFor='text-input'
          shrink
          sx={{
            fontSize: 20,
            color: props.readOnly
              ? lightTheme.palette.text.disabled
              : lightTheme.palette.text.secondary,
            '&.Mui-focused': {
              color: lightTheme.palette.text.primary,
            },
          }}
        >
          {label}
        </InputLabelStyled>
      )}
      <TextInputBase
        id='text-input'
        placeholder={placeholder}
        type={type}
        inputProps={{ min: 0 }}
        {...wheelHandler}
        {...props}
      />
      {props.readOnly && showCopyButton && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: label ? '46px' : '22px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <CopyButton onClick={handleCopy} size='small' aria-label='copy text'>
            {copied ? <CheckIcon width={16} /> : <ContentCopyIcon fontSize='small' />}
          </CopyButton>
        </Box>
      )}
      {props.error ? (
        <FormHelperText error>{errorText}</FormHelperText>
      ) : (
        helperText && <FormHelperText>{helperText}</FormHelperText>
      )}
    </StyledFormControl>
  )
}

export default TextInput
